import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Mail, Lock, User as UserIcon, Loader2, Check } from "lucide-react";
import { motion } from "framer-motion";
import { validateEmail, validatePassword } from "./LoginPage";
import GoogleSignInButton from "@/components/shared/GoogleSignInButton";
import { toast } from "sonner";

export default function RegisterPage() {
    const { register, loginWithGoogle, isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();
    // La landing manda ?rol=merchant&email=… desde sus CTAs para precargar.
    const [params] = useSearchParams();
    const [name, setName] = useState("");
    const [email, setEmail] = useState(() => params.get("email") || "");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [role, setRole] = useState(() =>
        params.get("rol") === "merchant" ? "merchant" : "customer"
    );
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [showPwd, setShowPwd] = useState(false);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const strength = passwordStrength(password);

    if (isLoading) return null;
    if (isAuthenticated) return <Navigate to="/" replace />;

    const validate = () => {
        const e = {};
        if (!name.trim() || name.trim().length < 2) e.name = "Ingresa tu nombre completo.";
        if (!email.trim()) e.email = "Ingresa tu correo.";
        else if (!validateEmail(email)) e.email = "Correo inválido.";
        if (!password) e.password = "Crea una contraseña.";
        else if (!validatePassword(password))
            e.password = "Mínimo 8 caracteres.";
        if (confirm !== password) e.confirm = "Las contraseñas no coinciden.";
        if (!acceptTerms) e.terms = "Acepta los términos para continuar.";
        return e;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length) return;
        setSubmitting(true);
        setServerError(null);
        try {
            const user = await register({
                name: name.trim(),
                email: email.trim(),
                password,
                role,
            });
            navigate(user.role === "merchant" ? "/dashboard" : "/", {
                replace: true,
            });
        } catch (err) {
            setServerError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleGoogle = async (credential) => {
        setServerError(null);
        try {
            // Pass the role the user picked in the form so a NEW account is
            // created with the right role (existing accounts keep their role).
            const u = await loginWithGoogle(credential, role);
            navigate(u.role === "merchant" ? "/dashboard" : "/", { replace: true });
        } catch (err) {
            setServerError(err.message);
        }
    };

    return (
        <div
            data-testid="register-page"
            className="min-h-[calc(100vh-72px)] grid place-items-center px-4 py-10 sm:py-16 overflow-x-hidden"
        >
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-md min-w-0"
            >
                <div className="text-center mb-8">
                    <Link
                        to="/"
                        aria-label="Tiendify — Inicio"
                        className="inline-flex items-center justify-center mb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 rounded-md"
                    >
                        <img
                            src="/assets/images/t-mark.png"
                            alt="Tiendify"
                            width="48"
                            height="48"
                            className="w-12 h-12 object-contain"
                        />
                    </Link>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
                        Crea tu cuenta gratis
                    </h1>
                    <p className="mt-2 text-sm text-zinc-600">
                        Sin tarjeta, sin compromiso, sin renta mensual.
                    </p>
                </div>

                <form
                    onSubmit={onSubmit}
                    noValidate
                    className="bg-white border border-zinc-200 rounded-2xl p-5 sm:p-8 shadow-sm space-y-5 min-w-0"
                >
                    <GoogleSignInButton
                        text="signup_with"
                        onCredential={handleGoogle}
                        onError={(e) => {
                            console.error(e);
                            toast.error("No se pudo registrarse con Google");
                        }}
                    />

                    <div className="flex items-center gap-3 text-xs text-zinc-600">
                        <div className="flex-1 h-px bg-zinc-200" />
                        <span>o con tu correo</span>
                        <div className="flex-1 h-px bg-zinc-200" />
                    </div>

                    {/* Role selector */}
                    <div data-testid="role-selector">
                        <p className="text-xs font-medium text-zinc-700 mb-2">
                            ¿Qué quieres hacer en Tiendify?
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            <RoleTile
                                active={role === "customer"}
                                onClick={() => setRole("customer")}
                                title="Comprar"
                                body="Descubrir y comprar"
                                testid="role-customer"
                            />
                            <RoleTile
                                active={role === "merchant"}
                                onClick={() => setRole("merchant")}
                                title="Vender"
                                body="Crear mi tienda"
                                testid="role-merchant"
                            />
                        </div>
                    </div>

                    <Field
                        id="name"
                        label="Nombre completo"
                        icon={UserIcon}
                        error={errors.name}
                        testid="register-name"
                    >
                        <Input
                            id="name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onBlur={() => setErrors((p) => ({ ...p, name: validate().name }))}
                            placeholder="Ana García"
                            autoComplete="name"
                            data-testid="register-name-input"
                            className="pl-10 h-11"
                        />
                    </Field>

                    <Field
                        id="email"
                        label="Correo electrónico"
                        icon={Mail}
                        error={errors.email}
                        testid="register-email"
                    >
                        <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onBlur={() => setErrors((p) => ({ ...p, email: validate().email }))}
                            placeholder="tu@correo.com"
                            autoComplete="email"
                            data-testid="register-email-input"
                            className="pl-10 h-11"
                        />
                    </Field>

                    <Field
                        id="password"
                        label="Contraseña"
                        icon={Lock}
                        error={errors.password}
                        testid="register-password"
                        rightAction={
                            <button
                                type="button"
                                onClick={() => setShowPwd((s) => !s)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                                aria-label={showPwd ? "Ocultar" : "Mostrar"}
                            >
                                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        }
                    >
                        <Input
                            id="password"
                            type={showPwd ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Mínimo 8 caracteres"
                            autoComplete="new-password"
                            data-testid="register-password-input"
                            className="pl-10 pr-10 h-11"
                        />
                    </Field>

                    {password && (
                        <div className="flex gap-1 -mt-3" data-testid="register-password-strength">
                            {[0, 1, 2, 3].map((i) => (
                                <span
                                    key={i}
                                    className={`h-1 flex-1 rounded-full transition-colors ${
                                        strength.score > i
                                            ? strength.color
                                            : "bg-zinc-200"
                                    }`}
                                />
                            ))}
                        </div>
                    )}

                    <Field
                        id="confirm"
                        label="Confirma tu contraseña"
                        icon={Lock}
                        error={errors.confirm}
                        testid="register-confirm"
                    >
                        <Input
                            id="confirm"
                            type={showPwd ? "text" : "password"}
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            placeholder="Repite tu contraseña"
                            autoComplete="new-password"
                            data-testid="register-confirm-input"
                            className="pl-10 h-11"
                        />
                    </Field>

                    <label className="flex items-start gap-2 cursor-pointer text-xs text-zinc-600">
                        <input
                            type="checkbox"
                            checked={acceptTerms}
                            onChange={(e) => setAcceptTerms(e.target.checked)}
                            data-testid="register-accept-terms"
                            className="mt-0.5"
                        />
                        <span>
                            Acepto los{" "}
                            <Link
                                to="/terminos"
                                target="_blank"
                                className="text-primary hover:underline"
                            >
                                Términos y condiciones
                            </Link>{" "}
                            y el{" "}
                            <Link
                                to="/privacidad"
                                target="_blank"
                                className="text-primary hover:underline"
                            >
                                Aviso de privacidad
                            </Link>
                            .
                        </span>
                    </label>
                    {errors.terms && (
                        <p className="text-xs text-red-600 -mt-3">{errors.terms}</p>
                    )}

                    {serverError && (
                        <p
                            data-testid="register-error"
                            className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
                        >
                            {serverError}
                        </p>
                    )}

                    <Button
                        type="submit"
                        data-testid="register-submit"
                        disabled={submitting}
                        className="w-full h-11 bg-primary hover:bg-primary-hover text-primary-foreground font-medium"
                    >
                        {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Crear mi cuenta
                    </Button>
                </form>

                <p className="mt-6 text-center text-sm text-zinc-600">
                    ¿Ya tienes cuenta?{" "}
                    <Link
                        to="/login"
                        data-testid="register-go-login"
                        className="font-medium text-primary hover:text-primary-hover"
                    >
                        Iniciar sesión
                    </Link>
                </p>
            </motion.div>
        </div>
    );
}

function Field({ id, label, icon: Icon, error, testid, rightAction, children }) {
    return (
        <div data-testid={testid}>
            <Label htmlFor={id} className="text-xs font-medium text-zinc-700">
                {label}
            </Label>
            <div className="relative mt-1.5">
                {Icon && (
                    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                )}
                {children}
                {rightAction}
            </div>
            {error && (
                <p className="mt-1.5 text-xs text-red-600" data-testid={`${testid}-error`}>
                    {error}
                </p>
            )}
        </div>
    );
}

function RoleTile({ active, onClick, title, body, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`p-3 rounded-lg border text-left transition-all ${
                active
                    ? "border-indigo-600 bg-indigo-50/50 ring-2 ring-indigo-100"
                    : "border-zinc-200 hover:border-zinc-300"
            }`}
        >
            <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-zinc-900">{title}</span>
                {active && <Check className="w-3.5 h-3.5 text-indigo-600" />}
            </div>
            <p className="text-xs text-zinc-500 mt-0.5">{body}</p>
        </button>
    );
}

function passwordStrength(p) {
    if (!p) return { score: 0, color: "bg-zinc-200" };
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p) || p.length >= 12) s++;
    const colors = ["bg-red-400", "bg-orange-400", "bg-yellow-400", "bg-green-500"];
    return { score: s, color: colors[Math.min(s - 1, 3)] || "bg-zinc-200" };
}
